import React, { Component } from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import './App.css';
import './index.css';
import './bootstrap-4/css/bootstrap.min.css';
import { ThemeContext } from './components/Core/ThemeContext';
import { SmallGutter, LargeGutter, MiniTitle } from './components/Core/TextComponents';
import IconGrid from './components/Input/IconGrid';
import { InfoOutlined, SearchOutlined } from '@material-ui/icons';
import { CallSplit } from '@material-ui/icons';
import { DeleteOutlined } from '@material-ui/icons';
import CodeOutlinedIcon from '@material-ui/icons/CodeOutlined';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import LanguageIcon from '@material-ui/icons/Language';
import { Divider } from '@material-ui/core';
import { trackCustomEvent } from './utilities/analytics';

const toolsPageStyles = {
    page: {
        minHeight: '100vh',
        padding: 'clamp(16px, 3vw, 28px)',
        background: 'linear-gradient(180deg, #f7f8f2 0%, #edf3f2 44%, #f8fbfb 100%)',
    },
    frame: {
        width: 'min(100%, 980px)',
        margin: '0 auto',
    },
    stack: {
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
    },
    heroShell: {
        display: 'flex',
        background: 'linear-gradient(135deg, #fffdfa 0%, #f5fbfa 100%)',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid rgba(188, 198, 208, 0.4)',
        boxShadow: '0 14px 32px rgba(86, 96, 110, 0.1)',
    },
    heroStrip: {
        width: 18,
        minWidth: 18,
        background: 'var(--color-grad-1)',
    },
    heroBody: {
        flex: 1,
        padding: 'clamp(18px, 3vw, 26px)',
    },
    heroEyebrow: {
        margin: 0,
        color: 'rgba(90, 105, 108, 0.84)',
        fontSize: '0.74em',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    heroTitle: {
        margin: '6px 0 0 0',
        fontSize: 'clamp(1.9em, 4vw, 2.45em)',
        color: 'rgba(0, 0, 0, 0.82)',
        fontFamily: 'Dancing Script, Georgia',
        lineHeight: 1.1,
    },
    heroSummary: {
        margin: '12px 0 0 0',
        color: 'rgba(0, 0, 0, 0.7)',
        fontSize: '1em',
        lineHeight: 1.7,
        maxWidth: 720,
    },
    shell: {
        display: 'flex',
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(86, 96, 110, 0.08)',
        border: '1px solid rgba(188, 198, 208, 0.38)',
    },
    strip: {
        width: 12,
        minWidth: 12,
        background: 'var(--color-grad-1)',
    },
    content: {
        flex: 1,
        background: 'linear-gradient(180deg, #fffefb 0%, #fcfdfd 100%)',
    },
    summaryButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '16px clamp(16px, 2.6vw, 22px)',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        textAlign: 'left',
    },
    summaryText: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 0,
    },
    summaryEyebrow: {
        margin: 0,
        color: 'rgba(92, 108, 111, 0.82)',
        fontSize: '0.72em',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
    },
    summaryTitle: {
        margin: 0,
        color: 'rgba(0, 0, 0, 0.8)',
        fontSize: '1.05em',
        fontWeight: 700,
        letterSpacing: '0.01em',
    },
    summaryDivider: {
        height: 1,
        background: 'rgba(188, 198, 208, 0.32)',
        margin: '0 clamp(16px, 2.6vw, 22px)',
    },
    summaryBody: {
        padding: '16px clamp(16px, 2.6vw, 22px) 20px clamp(16px, 2.6vw, 22px)',
        color: 'rgba(0, 0, 0, 0.76)',
    },
    collapsedBody: {
        maxHeight: 0,
        overflow: 'hidden',
        paddingTop: 0,
        paddingBottom: 0,
    },
    helperText: {
        margin: '0 0 12px 0',
        color: 'rgba(0, 0, 0, 0.62)',
        fontSize: '0.96em',
        lineHeight: 1.65,
    },
};

function ToolsHero() {
    return (
        <div style={toolsPageStyles.heroShell}>
            <div style={toolsPageStyles.heroStrip} />
            <div style={toolsPageStyles.heroBody}>
                <p style={toolsPageStyles.heroEyebrow}>Workspace</p>
                <h1 style={toolsPageStyles.heroTitle}>Tools</h1>
                <p style={toolsPageStyles.heroSummary}>
                    Focused utilities and developer surfaces for ai-BIBLE,
                    gathered into a cleaner toolkit layout with the Bible-facing
                    tools first and maintenance controls separated below.
                </p>
            </div>
        </div>
    );
}

function ToolsSection({
    title,
    eyebrow,
    helperText,
    children,
    startsOpen = true,
}) {
    const [open, setOpen] = React.useState(startsOpen);

    return (
        <section style={toolsPageStyles.shell}>
            <div style={toolsPageStyles.strip} aria-hidden="true" />
            <div style={toolsPageStyles.content}>
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    style={toolsPageStyles.summaryButton}
                >
                    <span style={toolsPageStyles.summaryText}>
                        <span style={toolsPageStyles.summaryEyebrow}>{eyebrow}</span>
                        <h2 style={toolsPageStyles.summaryTitle}>{title}</h2>
                    </span>
                    <ExpandMoreIcon
                        style={{
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 140ms ease',
                            color: 'rgba(83, 102, 106, 0.85)',
                        }}
                    />
                </button>
                <div style={toolsPageStyles.summaryDivider} aria-hidden="true" />
                <div
                    aria-hidden={!open}
                    style={{
                        ...toolsPageStyles.summaryBody,
                        ...(open ? null : toolsPageStyles.collapsedBody),
                    }}
                >
                    {helperText ? <p style={toolsPageStyles.helperText}>{helperText}</p> : null}
                    {children}
                </div>
            </div>
        </section>
    );
}

class Labs extends Component {
    static contextType = ThemeContext;

    data = {
        bible: {
            icons: [SearchOutlined, CallSplit, LanguageIcon, CodeOutlinedIcon, ChatBubbleOutlineIcon, ChatBubbleOutlineIcon],
            labels: ['bible search', 'mcp server', 'web mcp', 'api docs', 'chat (old)', 'BibleAI GPT'],
            targets: ['/search', 'https://github.com/AdbC99/ai-bible', '/web-mcp-support', `${window.API_BASE || ''}/docs`, '/Chat', 'https://chatgpt.com/g/g-68dc811398b08191a51676d58926cbe4-bible-ai'],
        },
        admin: {
            icons: [HelpOutlineOutlinedIcon, DeleteOutlined, InfoOutlined],
            labels: ['help', 'manage data', 'debug'],
            targets: ['/help', '/labs/deleteData', '/labs/debug'],
        },
    };

    handleIconClick = (index, targets) => {
        const target = targets[index];
        console.log(`Icon ${index} clicked ${target} `);
        trackCustomEvent({
            ev: `labs.${target}`,
            unique: false,
        });
        window.location = target;
    };

    constructor() {
        super();
        this.handleIconClick = this.handleIconClick.bind(this);
    }

    render() {
        return (
            <>
                <div style={toolsPageStyles.page}>
                    <div style={toolsPageStyles.frame}>
                        <div style={toolsPageStyles.stack}>
                            <ToolsHero />

                            <ToolsSection
                                title="Bible"
                                eyebrow="Reader Toolkit"
                                helperText="Search, docs, APIs, and chat surfaces that support the main ai-BIBLE workflow."
                            >
                                <IconGrid
                                    icons={this.data.bible.icons}
                                    labels={this.data.bible.labels}
                                    onIconClick={(index) => { this.handleIconClick(index, this.data.bible.targets); }}
                                />
                                <SmallGutter />
                            </ToolsSection>

                            <ToolsSection
                                title="Admin"
                                eyebrow="Maintenance"
                                helperText="Operational utilities for support, cleanup, and debugging."
                                startsOpen
                            >
                                <IconGrid
                                    icons={this.data.admin.icons}
                                    labels={this.data.admin.labels}
                                    onIconClick={(index) => { this.handleIconClick(index, this.data.admin.targets); }}
                                />
                                <SmallGutter />
                            </ToolsSection>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default Labs;
